//src/pages/LogsRecaptcha.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";

import {
  AlertTriangle,
  FileSpreadsheet,
  Search,
  ShieldCheck,
} from "lucide-react";

import * as XLSX from "xlsx";

import "./LogsRecaptcha.css";

export default function LogsRecaptcha() {
  const [logs, setLogs] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    carregarLogs();
  }, []);

  const carregarLogs = async () => {
    try {
      setLoading(true);

      const res = await axios.get("/recaptcha/logs");

      setLogs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Erro ao buscar logs:", err);
    } finally {
      setLoading(false);
    }
  };

  const logsFiltrados = useMemo(() => {
    const termo = filtro.toLowerCase();

    return logs.filter((log) => {
      return (
        (log.email || "").toLowerCase().includes(termo) ||
        (log.ip || "").toLowerCase().includes(termo) ||
        (log.origem || "").toLowerCase().includes(termo) ||
        (log.motivo || "").toLowerCase().includes(termo)
      );
    });
  }, [logs, filtro]);

  const resumo = useMemo(() => {
    const total = logs.length;

    const suspeitos = logs.filter((l) => Number(l.score) < 0.5).length;

    const scoreAlto = logs.filter((l) => Number(l.score) >= 0.7).length;

    const scoreMedio = logs.filter(
      (l) => Number(l.score) >= 0.5 && Number(l.score) < 0.7
    ).length;

    return {
      total,
      suspeitos,
      scoreAlto,
      scoreMedio,
    };
  }, [logs]);

  const exportarExcel = () => {
    const dados = logsFiltrados.map((l) => ({
      ID: l.id,
      Email: l.email,
      IP: l.ip,
      Score: l.score,
      Origem: l.origem,
      Motivo: l.motivo,
      Data: new Date(l.data).toLocaleString("pt-BR"),
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dados);

    XLSX.utils.book_append_sheet(wb, ws, "reCAPTCHA Logs");

    XLSX.writeFile(wb, "logs-recaptcha.xlsx");
  };

  const formatarData = (data) => {
    if (!data) return "—";

    try {
      return new Date(data).toLocaleString("pt-BR");
    } catch {
      return "—";
    }
  };

  const getScoreBadge = (score) => {
    const valor = Number(score);

    if (valor >= 0.7) {
      return <span className="logsRecaptchaAdmin-badge ok">Seguro</span>;
    }

    if (valor >= 0.5) {
      return <span className="logsRecaptchaAdmin-badge warn">Moderado</span>;
    }

    return <span className="logsRecaptchaAdmin-badge danger">Suspeito</span>;
  };

  return (
    <div className="logsRecaptchaAdmin-page">
      <div className="logsRecaptchaAdmin-topbar">
        <div>
          <span className="logsRecaptchaAdmin-kicker">
            Segurança da plataforma
          </span>

          <h2>Logs de reCAPTCHA</h2>

          <p>
            Monitore tentativas suspeitas, scores de validação e origens de
            acesso registradas pelo sistema.
          </p>
        </div>

        <button
          onClick={exportarExcel}
          className="logsRecaptchaAdmin-btn"
        >
          <FileSpreadsheet size={16} />
          Exportar Excel
        </button>
      </div>

      <div className="logsRecaptchaAdmin-cards">
        <div className="logsRecaptchaAdmin-card">
          <span>Total de registros</span>
          <strong>{resumo.total.toLocaleString("pt-BR")}</strong>
        </div>

        <div className="logsRecaptchaAdmin-card success">
          <span>Scores seguros</span>
          <strong>{resumo.scoreAlto.toLocaleString("pt-BR")}</strong>
        </div>

        <div className="logsRecaptchaAdmin-card warning">
          <span>Scores moderados</span>
          <strong>{resumo.scoreMedio.toLocaleString("pt-BR")}</strong>
        </div>

        <div className="logsRecaptchaAdmin-card danger">
          <span>Tentativas suspeitas</span>
          <strong>{resumo.suspeitos.toLocaleString("pt-BR")}</strong>
        </div>
      </div>

      <div className="logsRecaptchaAdmin-filtros">
        <div className="logsRecaptchaAdmin-search">
          <Search size={15} />

          <input
            placeholder="Buscar por IP, e-mail, origem ou motivo..."
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
          />
        </div>
      </div>

      <div className="logsRecaptchaAdmin-tableCard">
        <div className="logsRecaptchaAdmin-tableHeader">
          <h3>Registros encontrados</h3>

          <span>
            {logsFiltrados.length.toLocaleString("pt-BR")} resultado(s)
          </span>
        </div>

        <div className="logsRecaptchaAdmin-tableWrapper">
          <table className="logsRecaptchaAdmin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>IP</th>
                <th>Score</th>
                <th>Status</th>
                <th>Motivo</th>
                <th>Origem</th>
                <th>Data</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="logsRecaptchaAdmin-empty">
                    Carregando logs...
                  </td>
                </tr>
              ) : logsFiltrados.length === 0 ? (
                <tr>
                  <td colSpan="8" className="logsRecaptchaAdmin-empty">
                    Nenhum registro encontrado.
                  </td>
                </tr>
              ) : (
                logsFiltrados.map((log) => (
                  <tr key={log.id}>
                    <td>
                      <strong>#{log.id}</strong>
                    </td>

                    <td>
                      <div className="logsRecaptchaAdmin-user">
                        <div className="logsRecaptchaAdmin-avatar">
                          {(log.email || "R").charAt(0).toUpperCase()}
                        </div>

                        <span>{log.email || "—"}</span>
                      </div>
                    </td>

                    <td>
                      <code>{log.ip || "—"}</code>
                    </td>

                    <td>
                      <strong>{Number(log.score || 0).toFixed(2)}</strong>
                    </td>

                    <td>{getScoreBadge(log.score)}</td>

                    <td>
                      <div className="logsRecaptchaAdmin-motivo">
                        <AlertTriangle size={13} />
                        {log.motivo || "—"}
                      </div>
                    </td>

                    <td>
                      <div className="logsRecaptchaAdmin-origem">
                        <ShieldCheck size={13} />
                        {log.origem || "—"}
                      </div>
                    </td>

                    <td>{formatarData(log.data)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}